/**
 * THE PROOF SURFACE: render the real graph through an OfflineAudioContext and hand back the
 * samples.
 *
 * -- WHY THIS EXISTS SEPARATELY FROM THE TESTS -----------------------------------
 *
 * `sea.test.ts` measures the Float32Arrays this repository generates. That is necessary and it
 * is not sufficient, because between the generator and a listener there is a browser: a buffer
 * whose sample rate does not match the context gets resampled, a loop point can be rounded, a
 * gain can be applied twice. Every one of those would put a click in a signal that measured
 * clean in Node.
 *
 * So this builds the SAME graph the game plays - the same two loops, the same gains, the same
 * one-shots - inside an OfflineAudioContext, renders it faster than real time, and returns what
 * came out. The numbers in this round's report for the delivered sound are taken from here, and
 * the numbers for the generator are taken from the tests, and they are reported as two separate
 * measurements because they can disagree and the disagreement is the interesting case.
 *
 * It is a few dozen lines in the shipped bundle and it earns them: it is also how a screenshot
 * script or a later critic re-measures this lane without trusting a word written here.
 */

import { renderMomentBank, type MomentBank } from './moments';
import { renderSea } from './sea';
import type { Samples } from './samples';

/** A one-shot to place in the capture, by name and by when. */
export interface CaptureMoment {
  /** `land0` .. `land4` for the weight buckets, or `junk` / `record` / `lift` / `settle` / `mark`. */
  readonly name: string;
  /** Seconds from the start of the capture. */
  readonly at: number;
}

export interface CaptureOptions {
  readonly seconds: number;
  readonly sampleRate?: number;
  /** Where each sea loop starts in its own buffer. Zeros put the loop seam at a known instant. */
  readonly startOffsets?: readonly number[];
  /** Leave the sea out, to measure a moment on its own. */
  readonly sea?: boolean;
  readonly moments?: readonly CaptureMoment[];
}

function pickMoment(bank: MomentBank, name: string): Float32Array | null {
  if (name.startsWith('land')) {
    const bucket = Number(name.slice(4)) || 0;
    return bank.land[Math.max(0, Math.min(bank.land.length - 1, bucket))]?.[0] ?? null;
  }
  if (name === 'junk') return bank.junk[0] ?? null;
  if (name === 'record') return bank.record[0] ?? null;
  if (name === 'mark') return bank.mark[0] ?? null;
  if (name === 'lift') return bank.lift;
  if (name === 'settle') return bank.settle;
  return null;
}

/**
 * Render and return one Float32Array per output channel.
 *
 * Stereo, because the surface layer is stereo and a mono render would average the two
 * decorrelated sides into something 3 dB quieter and narrower than anybody hears.
 */
export async function captureOffline(options: CaptureOptions): Promise<Float32Array[]> {
  const sampleRate = options.sampleRate ?? 48000;
  const frames = Math.round(options.seconds * sampleRate);
  const ctx = new OfflineAudioContext(2, frames, sampleRate);
  const master = ctx.createGain();
  master.gain.value = 1;
  master.connect(ctx.destination);

  if (options.sea !== false) {
    const layers = renderSea(sampleRate);
    layers.forEach((layer, i) => {
      const buf = ctx.createBuffer(layer.channels.length, layer.length, layer.sampleRate);
      for (let c = 0; c < layer.channels.length; c++) buf.copyToChannel(layer.channels[c] as Samples, c);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      src.connect(master);
      src.start(0, options.startOffsets?.[i] ?? 0);
    });
  }

  const moments = options.moments ?? [];
  if (moments.length > 0) {
    const bank = renderMomentBank(sampleRate);
    for (const m of moments) {
      const data = pickMoment(bank, m.name);
      if (data === null) continue;
      const buf = ctx.createBuffer(1, data.length, sampleRate);
      buf.copyToChannel(data as Samples, 0);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(master);
      src.start(m.at);
    }
  }

  const rendered = await ctx.startRendering();
  const out: Float32Array[] = [];
  for (let c = 0; c < rendered.numberOfChannels; c++) {
    // A fresh copy: getChannelData hands back a view the context still owns.
    out.push(Float32Array.from(rendered.getChannelData(c)));
  }
  return out;
}
